import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Plus, Save, X, Edit, Trash2, GripVertical, Users } from 'lucide-react';

interface DriverGroup {
  id: string;
  name: string;
  colour_hex: string | null;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

const DEFAULT_COLORS = [
  '#3B82F6', '#6366F1', '#10B981', '#8B5CF6', '#EF4444',
  '#F97316', '#EC4899', '#14B8A6', '#F59E0B', '#64748B',
  '#2563EB', '#0EA5E9', '#7C3AED', '#D946EF', '#84CC16',
];

const RosterGroupsManagement: React.FC = () => {
  const [groups, setGroups] = useState<DriverGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<DriverGroup>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState<Partial<DriverGroup>>({
    name: '',
    description: '',
    colour_hex: '#3B82F6',
    sort_order: 100,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('roster_driver_groups')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error loading groups:', error);
      toast({ title: 'Error', description: 'Failed to load driver groups', variant: 'destructive' });
    } else {
      setGroups(data || []);
    }
    setLoading(false);
  };

  const startEdit = (g: DriverGroup) => {
    setEditingId(g.id);
    setEditForm({ ...g });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.name?.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('roster_driver_groups')
      .update({
        name: editForm.name.trim(),
        description: editForm.description?.trim() || null,
        colour_hex: editForm.colour_hex || '#3B82F6',
        sort_order: editForm.sort_order ?? 100,
        is_active: editForm.is_active ?? true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', editingId);

    if (error) {
      toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `Group "${editForm.name}" updated` });
      setEditingId(null);
      setEditForm({});
      loadGroups();
    }
    setSaving(false);
  };

  const addGroup = async () => {
    if (!newForm.name?.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    if (groups.some(g => g.name.toLowerCase() === newForm.name?.trim().toLowerCase())) {
      toast({ title: 'Error', description: 'A group with this name already exists', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('roster_driver_groups')
      .insert({
        name: newForm.name.trim(),
        description: newForm.description?.trim() || null,
        colour_hex: newForm.colour_hex || '#3B82F6',
        sort_order: newForm.sort_order ?? 100,
        is_active: newForm.is_active ?? true,
      });

    if (error) {
      toast({ title: 'Error', description: 'Failed to add: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `Group "${newForm.name}" added` });
      setShowAddForm(false);
      setNewForm({ name: '', description: '', colour_hex: '#3B82F6', sort_order: 100, is_active: true });
      loadGroups();
    }
    setSaving(false);
  };

  const deleteGroup = async (id: string, name: string) => {
    if (!confirm(`Delete group "${name}"? This cannot be undone.\n\nNote: Drivers assigned to this group will keep the group name in their profile until manually removed.`)) return;
    const { error } = await supabase.from('roster_driver_groups').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Deleted', description: `Group "${name}" removed` });
      loadGroups();
    }
  };

  const renderForm = (form: Partial<DriverGroup>, setForm: (f: Partial<DriverGroup>) => void, isNew: boolean) => (
    <div className="space-y-4 p-4 rounded-lg border border-gray-600 bg-gray-800/50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-yellow-500 text-xs">Group Name</Label>
          <Input
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Night Crew"
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Description</Label>
          <Input
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional description"
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Sort Order</Label>
          <Input
            type="number"
            value={form.sort_order ?? 100}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })}
          />
        </div>
      </div>

      {/* Color picker */}
      <div>
        <Label className="text-yellow-500 text-xs">Colour</Label>
        <div className="flex items-center gap-3 mt-1">
          <input
            type="color"
            value={form.colour_hex || '#3B82F6'}
            onChange={(e) => setForm({ ...form, colour_hex: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-600 bg-transparent"
          />
          <Input
            value={form.colour_hex || '#3B82F6'}
            onChange={(e) => setForm({ ...form, colour_hex: e.target.value })}
            className="w-28 font-mono text-sm"
            placeholder="#3B82F6"
          />
          <div className="flex gap-1 flex-wrap">
            {DEFAULT_COLORS.map(c => (
              <button
                key={c}
                onClick={() => setForm({ ...form, colour_hex: c })}
                className={`w-6 h-6 rounded-full border-2 transition-all ${form.colour_hex === c ? 'border-white scale-110' : 'border-transparent hover:border-gray-400'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Active flag */}
      <div className="flex items-center gap-2">
        <Checkbox
          id={`flag-active-group-${isNew ? 'new' : 'edit'}`}
          checked={form.is_active ?? true}
          onCheckedChange={(c) => setForm({ ...form, is_active: c as boolean })}
        />
        <Label htmlFor={`flag-active-group-${isNew ? 'new' : 'edit'}`} className="text-xs">Active</Label>
      </div>

      {/* Preview */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
        <span className="text-xs text-gray-400">Preview:</span>
        <div
          className="px-3 py-1 rounded text-white text-sm font-medium flex items-center gap-1.5"
          style={{ backgroundColor: form.colour_hex || '#3B82F6' }}
        >
          <Users className="w-3.5 h-3.5" />
          {form.name || 'Group Name'}
        </div>
        <Badge
          variant="secondary"
          className="text-xs"
          style={{
            backgroundColor: (form.colour_hex || '#3B82F6') + '20',
            color: form.colour_hex || '#3B82F6',
            borderColor: (form.colour_hex || '#3B82F6') + '40',
          }}
        >
          {form.name || 'Group Name'}
        </Badge>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={isNew ? addGroup : saveEdit}
          disabled={saving}
          size="sm"
          className="bg-green-600 hover:bg-green-700"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : isNew ? 'Add Group' : 'Save Changes'}
        </Button>
        <Button
          onClick={isNew ? () => setShowAddForm(false) : cancelEdit}
          variant="outline"
          size="sm"
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-yellow-500 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Roster Groups
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Manage driver groups used for rostering and scheduling. Groups help categorise drivers for shift assignment.
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Group
          </Button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && renderForm(newForm, setNewForm, true)}

      {/* Groups List */}
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.id}>
            {editingId === g.id ? (
              renderForm(editForm, setEditForm, false)
            ) : (
              <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${g.is_active ? 'border-gray-700 bg-gray-800/30 hover:bg-gray-800/60' : 'border-gray-800 bg-gray-900/30 opacity-60'}`}>
                <GripVertical className="w-4 h-4 text-gray-600 flex-shrink-0" />

                {/* Color dot */}
                <div
                  className="w-5 h-5 rounded-full flex-shrink-0 border border-gray-600"
                  style={{ backgroundColor: g.colour_hex || '#3B82F6' }}
                />

                {/* Name badge */}
                <Badge
                  className="font-medium text-xs flex-shrink-0 text-white"
                  style={{
                    backgroundColor: g.colour_hex || '#3B82F6',
                  }}
                >
                  <Users className="w-3 h-3 mr-1" />
                  {g.name}
                </Badge>

                {/* Description */}
                <div className="flex-1 min-w-0">
                  {g.description && (
                    <span className="text-xs text-gray-500">{g.description}</span>
                  )}
                </div>

                {/* Active flag */}
                {!g.is_active && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Inactive</Badge>}

                {/* Sort order */}
                <span className="text-xs text-gray-500 w-8 text-right flex-shrink-0">#{g.sort_order}</span>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => startEdit(g)}
                    className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteGroup(g.id, g.name)}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {groups.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No groups configured. Click "Add Group" to create one.
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterGroupsManagement;
